import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { DataGrid } from '@/components/DataGrid/DataGrid';
import { useClientDataGrid } from '@/components/DataGrid/useClientDataGrid';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAlert } from '@/components/ConfirmDialog';
import { CountryThresholdsPanel } from './CountryThresholdsPanel';
import { DealersPanel } from './DealersPanel';
import { DealerTextFilter } from './DealerTextFilter';
import { downloadExport, ExportSessionExpiredError } from './exportDownload';
import { StellantisDealerMap, type StellantisDealerMapLabels } from './StellantisDealerMap';
import { waveLabel } from './takataFormat';
import { useDealersDashboard, type Dealer } from './useDealers';
import { categoryOf, UNKNOWN_CATEGORY_COLOR } from './useTakataDealers';
import '@/styles/stla/page-shell.scss';
import '@/styles/stla/stla_shared.scss';
import '@/styles/stla/stla_datatables.scss';
import '@/styles/stla/stla_dashboard.scss';
import '@/styles/stla/dealer-dashboard.scss';

/**
 * Replaces `Pages/Stellantis/DealerList.cshtml` at `/stla/index`.
 *
 * The wave arrives whole and every filter runs in the browser, as it did once
 * `window.dealersData` was in hand — the set now comes from
 * `GET /api/stla/dealers` instead of a serialised `<script>` block. Map, KPI
 * tiles and table all read the same filtered array.
 *
 * The Takata sibling is `TakataDealersPage`. They look alike and share their
 * panel, filter box and map plumbing, but the data differs in ways that are not
 * cosmetic: this screen has a car-type and brand filter, narrows its city
 * suggestions to the selected country, and counts calls from garages that never
 * booked an appointment towards its Total Calls tile.
 */

/**
 * After Sales thresholds are four day bands, the last being Critical, so the
 * country panel shows all four. Takata keeps the panel's 1–3 default.
 */
const AFTER_SALES_THRESHOLD_CATEGORIES = [1, 2, 3, 4] as const;

/** Stored values, not localized labels — the filter matches them literally. */
const CAR_TYPE_OPTIONS = [
  { value: 'Mainstream', label: 'Passenger Car Mainstream' },
  { value: 'Premium', label: 'Passenger Car Premium' },
  { value: 'LCV', label: 'LCV' },
];

const DEALER_SERVICE_OPTIONS = ['SERVICE ONLY', 'DEALER', 'NOT AVAILABLE'];

interface DealerFilters {
  country: string;
  carType: string;
  brand: string;
  dealerService: string;
  performance: string;
  city: string;
  dealer: string;
}

const NO_FILTERS: DealerFilters = {
  country: '',
  carType: '',
  brand: '',
  dealerService: '',
  performance: '',
  city: '',
  dealer: '',
};

/**
 * Substring match, case-insensitive. Unlike the Takata screen there is no
 * two-character minimum: `stla_dashboard.js` filtered from the first keystroke.
 */
function contains(value: string | null | undefined, term: string): boolean {
  return term === '' || (value ?? '').toLowerCase().includes(term);
}

export function DealersPage() {
  const { t } = useTranslation('mystery/dealerlist');
  const alert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();

  const waveParam = searchParams.get('wave');
  const { data, isLoading, isError } = useDealersDashboard(waveParam);
  const wave = waveParam ?? data?.selectedWave ?? null;

  const [filters, setFilters] = useState<DealerFilters>(NO_FILTERS);
  const debouncedFilters = useDebouncedValue(filters, 250);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [thresholdsPanelOpen, setThresholdsPanelOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const debouncedTableSearch = useDebouncedValue(tableSearch, 250);
  const [exporting, setExporting] = useState(false);

  const setFilter = useCallback((key: keyof DealerFilters, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const dealers = useMemo(() => data?.dealers ?? [], [data]);
  const categories = useMemo(() => data?.performanceCategories ?? {}, [data]);

  /**
   * Picking a country also clears a city that is not in it, so the two filters
   * cannot contradict each other and leave the table empty for no visible
   * reason. The Razor page did the same on its datalist.
   */
  const handleCountryChange = useCallback(
    (country: string) => {
      setFilters((current) => {
        const cityStillValid =
          current.city === '' ||
          country === '' ||
          data?.cityToCountry[current.city] === country;

        return { ...current, country, city: cityStillValid ? current.city : '' };
      });
    },
    [data],
  );

  const filteredDealers = useMemo(() => {
    const city = debouncedFilters.city.trim().toLowerCase();
    const dealerName = debouncedFilters.dealer.trim().toLowerCase();
    const performance = debouncedFilters.performance;

    return dealers.filter((dealer) => {
      if (debouncedFilters.brand && dealer.brand !== debouncedFilters.brand) return false;
      if (debouncedFilters.carType && dealer.carType !== debouncedFilters.carType) return false;
      if (debouncedFilters.country && dealer.country !== debouncedFilters.country) return false;
      if (!contains(dealer.city, city)) return false;
      if (!contains(dealer.name, dealerName)) return false;
      if (debouncedFilters.dealerService && dealer.dealerService !== debouncedFilters.dealerService)
        return false;
      if (performance !== '' && dealer.categoryIndex !== Number(performance)) return false;
      return true;
    });
  }, [dealers, debouncedFilters]);

  /**
   * Call-only garages under the same filters. They carry no performance
   * category, so an active performance filter excludes all of them rather than
   * matching some — otherwise the Total Calls tile would count calls that the
   * filter had already ruled out of the table.
   */
  const filteredNoAppointmentCalls = useMemo(() => {
    if (debouncedFilters.performance !== '') return [];

    const city = debouncedFilters.city.trim().toLowerCase();
    const dealerName = debouncedFilters.dealer.trim().toLowerCase();

    return (data?.noAppointmentCalls ?? []).filter((calls) => {
      if (debouncedFilters.brand && calls.brand !== debouncedFilters.brand) return false;
      if (debouncedFilters.carType && calls.carType !== debouncedFilters.carType) return false;
      if (debouncedFilters.country && calls.country !== debouncedFilters.country) return false;
      if (!contains(calls.city, city)) return false;
      if (!contains(calls.name, dealerName)) return false;
      if (debouncedFilters.dealerService && calls.dealerService !== debouncedFilters.dealerService)
        return false;
      return true;
    });
  }, [data, debouncedFilters]);

  /**
   * KPI tiles, recomputed from whatever the filters left.
   *
   * The three predicates match `DashboardDataService.GetDashboardData`'s, which
   * is what the tiles show before any filter is touched — a mismatch would make
   * the numbers jump the first time the user changed a dropdown. Note that
   * appointments count every dealer with a recorded wait, including ones the
   * supplier left uncategorised, while the average covers only the categorised.
   */
  const kpis = useMemo(() => {
    const categorised = filteredDealers.filter(
      (d) => (d.categoryIndex ?? 0) > 0 && d.averageWaitDays != null,
    );

    const averageWaitDays =
      categorised.length > 0
        ? categorised.reduce((sum, d) => sum + (d.averageWaitDays ?? 0), 0) / categorised.length
        : 0;

    return {
      averageWaitDays,
      totalCalls:
        filteredDealers.reduce((sum, d) => sum + (d.totalCalls ?? 0), 0) +
        filteredNoAppointmentCalls.reduce((sum, c) => sum + (c.totalCalls ?? 0), 0),
      totalAppointments: filteredDealers.filter((d) => d.averageWaitDays != null).length,
    };
  }, [filteredDealers, filteredNoAppointmentCalls]);

  const selectedCountryThresholds = useMemo(() => {
    if (!filters.country || !data) return null;
    const code = data.countryCodes[filters.country];
    const thresholds = code ? data.thresholdsByCountry[code] : undefined;
    return thresholds && thresholds.length > 0 ? thresholds : null;
  }, [filters.country, data]);

  /** City suggestions narrow to the selected country, as the Razor datalist did. */
  const cities = useMemo(() => {
    const all = data?.cities ?? [];
    if (!filters.country || !data) return all;
    return all.filter((city) => data.cityToCountry[city] === filters.country);
  }, [data, filters.country]);

  const dealerNames = useMemo(
    () => Array.from(new Set(dealers.map((d) => d.name))).sort((a, b) => a.localeCompare(b)),
    [dealers],
  );

  const mapLabels = useMemo<StellantisDealerMapLabels>(
    () => ({
      brand: t('Dashboard.Popup.Brand', 'Brand'),
      city: t('Dashboard.Popup.City', 'City'),
      address: t('Dashboard.Popup.Address', 'Address'),
      phone: t('Dashboard.Popup.Phone', 'Phone'),
      averageTime: t('Dashboard.Popup.AverageTime', 'Average Time'),
      calls: t('Dashboard.Popup.Calls', 'Calls'),
      answerRate: t('Dashboard.Popup.AnswerRate', 'Answer Rate'),
      days: t('Dashboard.Popup.Days', 'days'),
      noAppointment: t('Dashboard.Popup.NoAppointment', 'No appointment'),
    }),
    [t],
  );

  const columns = useMemo<ColumnDef<Dealer, unknown>[]>(
    () => [
      { id: 'country', accessorKey: 'country', header: t('Dashboard.Filters.Country', 'Country') },
      { id: 'oic', accessorKey: 'oic', header: t('Oic', 'OIC') },
      { id: 'physOut', accessorKey: 'physOut', header: t('PhysOut', 'PhysOut') },
      {
        id: 'name',
        accessorKey: 'name',
        header: t('Regional.DealersPanel.TableHeaders.Dealer', 'Company Name'),
      },
      { id: 'brand', accessorKey: 'brand', header: t('Dashboard.Popup.Brand', 'Brand') },
      { id: 'carType', accessorKey: 'carType', header: t('Grid.CarType', 'Car Type') },
      { id: 'city', accessorKey: 'city', header: t('Dashboard.Popup.City', 'City') },
      { id: 'address', accessorKey: 'address', header: t('Dashboard.Popup.Address', 'Address') },
      {
        id: 'categoryIndex',
        accessorKey: 'categoryIndex',
        header: t('Dashboard.Filters.Performance', 'Performance'),
        meta: { align: 'center' },
        cell: ({ row }) => {
          const category = categoryOf(categories, row.original.categoryIndex);
          return (
            <span
              className="performance-badge"
              style={{ backgroundColor: category?.backgroundColor ?? UNKNOWN_CATEGORY_COLOR }}
            >
              {category?.label ?? 'N/A'}
            </span>
          );
        },
      },
      {
        id: 'averageWaitDays',
        accessorKey: 'averageWaitDays',
        header: t('Regional.DealersPanel.TableHeaders.LeadBookingTime', 'Lead Booking Time'),
        meta: { align: 'center' },
        cell: ({ row }) =>
          row.original.categoryIndex === 0 || row.original.averageWaitDays == null
            ? t('Dashboard.Popup.NoAppointment', 'No appointment')
            : `${row.original.averageWaitDays.toFixed(1)} ${t('Dashboard.Popup.Days', 'days')}`,
      },
      { id: 'phone', accessorKey: 'phone', header: t('Dashboard.Popup.Phone', 'Phone') },
      {
        id: 'answerRate',
        accessorKey: 'answerRate',
        header: t('Grid.AnswerRate', 'Answer Rate (%)'),
        meta: { align: 'center' },
        cell: ({ row }) =>
          `${(row.original.totalCalls > 0 ? (row.original.answeredCalls / row.original.totalCalls) * 100 : 0).toFixed(1)}%`,
      },
    ],
    [t, categories],
  );

  const { gridProps } = useClientDataGrid<Dealer>({
    rows: filteredDealers,
    initialPageSize: 25,
    // Performance ascending, which is what `order: [[8, 'asc']]` selected —
    // column 8 is the Performance badge, not the Lead Booking Time its comment
    // claimed.
    initialSorting: [{ id: 'categoryIndex', desc: false }],
    search: debouncedTableSearch,
    searchFields: [
      'country',
      'oic',
      'physOut',
      'name',
      'brand',
      'carType',
      'city',
      'address',
      'phone',
    ],
  });

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (wave) params.set('wave', wave);
      for (const [key, value] of Object.entries(debouncedFilters)) {
        if (value) params.set(key, value);
      }
      await downloadExport(
        `/api/stla/dealers/export?${params.toString()}`,
        'Stellantis_Dealers_Export.xlsx',
      );
    } catch (error) {
      if (error instanceof ExportSessionExpiredError) {
        window.location.href = '/auth-login';
        return;
      }
      // `t` here is the mystery/dealerlist namespace; the title comes from shared.
      void alert({
        variant: 'danger',
        title: t('shared:Message.Error', 'Error'),
        message: t('Grid.ExportError', 'Could not export data. Please try again.'),
      });
    } finally {
      setExporting(false);
    }
  }, [exporting, wave, debouncedFilters, t, alert]);

  if (isError) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {t('Dashboard.LoadError', 'Could not load the dealer dashboard. Please try again.')}
      </div>
    );
  }

  return (
    // `dealer-dashboard` is what this screen's stylesheets are scoped to; see
    // styles/stla/page-shell.scss for why they have to be.
    <div className="regional-page dealer-dashboard">
      <div className="regional-container">
        {sidebarOpen && (
          <div className="sidebar">
            <div className="kpi-section">
              <h3>{t('Dashboard.KPI.MainTitle', 'Key figures')}</h3>
              <div className="kpi-grid">
                <div className="kpi-card primary">
                  <div className="kpi-label">
                    {t('Dashboard.KPI.AverageWaitTime', 'Average wait time')}
                  </div>
                  <div className="kpi-value">
                    <span>{kpis.averageWaitDays.toFixed(1)}</span> {t('Dashboard.KPI.Days', 'days')}
                  </div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">{t('Dashboard.KPI.Appointments', 'Appointments')}</div>
                  <div className="kpi-value">{kpis.totalAppointments}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">{t('Dashboard.KPI.TotalCalls', 'Total calls')}</div>
                  <div className="kpi-value">{kpis.totalCalls}</div>
                </div>
              </div>

              {/*
               * The tiles are computed from the filtered wave, so they all read 0
               * until it lands. The map overlay already announces the loading state;
               * one live region is enough, so this one is decorative.
               */}
              {isLoading && (
                <div className="loading-overlay" aria-hidden="true">
                  <div className="spinner" />
                </div>
              )}
            </div>

            {selectedCountryThresholds && (
              <div className="thresholds-section visible">
                <h4>{t('Dashboard.Thresholds.Title', 'Thresholds')}</h4>
                <div className="thresholds-list">
                  {selectedCountryThresholds.map((threshold) => (
                    <div className="threshold-item" key={threshold.categoryIndex}>
                      <span
                        className="threshold-badge"
                        style={{
                          backgroundColor:
                            categoryOf(categories, threshold.categoryIndex)?.backgroundColor ??
                            UNKNOWN_CATEGORY_COLOR,
                        }}
                      >
                        {threshold.categoryName}
                      </span>
                      <span className="threshold-value">{threshold.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="filters-section">
              <h3>{t('Dashboard.Filters.Title', 'Filters')}</h3>

              <div className="filter-group">
                <label htmlFor="waveFilter">Wave</label>
                <select
                  id="waveFilter"
                  value={wave ?? ''}
                  onChange={(event) => setSearchParams({ wave: event.target.value })}
                >
                  {(data?.availableWaves ?? []).map((w) => (
                    <option key={w} value={w}>
                      {waveLabel(w)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="countryFilter">{t('Dashboard.Filters.Country', 'Country')}</label>
                <select
                  id="countryFilter"
                  value={filters.country}
                  onChange={(event) => handleCountryChange(event.target.value)}
                >
                  <option value="">{t('Dashboard.Filters.AllCountries', 'All countries')}</option>
                  {(data?.countries ?? []).map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="typeCarFilter">{t('Dashboard.Filters.TypeCar', 'Car type')}</label>
                <select
                  id="typeCarFilter"
                  value={filters.carType}
                  onChange={(event) => setFilter('carType', event.target.value)}
                >
                  <option value="">{t('Dashboard.Filters.AllTypeCars', 'All car types')}</option>
                  {CAR_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="brandFilter">{t('Dashboard.Filters.Brand', 'Brand')}</label>
                <select
                  id="brandFilter"
                  value={filters.brand}
                  onChange={(event) => setFilter('brand', event.target.value)}
                >
                  <option value="">{t('Dashboard.Filters.AllBrands', 'All brands')}</option>
                  {(data?.brands ?? []).map((brand) => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="dealerServiceFilter">
                  {t('Dashboard.Filters.DealerService', 'Dealer service')}
                </label>
                <select
                  id="dealerServiceFilter"
                  value={filters.dealerService}
                  onChange={(event) => setFilter('dealerService', event.target.value)}
                >
                  <option value="">{t('Dashboard.Filters.All', 'All')}</option>
                  {DEALER_SERVICE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="performanceFilter">
                  {t('Dashboard.Filters.Performance', 'Performance')}
                </label>
                <select
                  id="performanceFilter"
                  value={filters.performance}
                  onChange={(event) => setFilter('performance', event.target.value)}
                >
                  <option value="">
                    {t('Dashboard.Filters.AllPerformances', 'All performances')}
                  </option>
                  {Object.values(categories)
                    .sort((a, b) => a.categoryIndex - b.categoryIndex)
                    .map((category) => (
                      <option key={category.categoryIndex} value={category.categoryIndex}>
                        {category.label}
                      </option>
                    ))}
                </select>
              </div>

              <DealerTextFilter
                id="cityFilter"
                label={t('Dashboard.Filters.City', 'City')}
                placeholder={t('Dashboard.Filters.FindCity', 'Find a city')}
                clearTitle={t('Dashboard.Filters.Clear', 'Clear')}
                listId="cityList"
                options={cities}
                value={filters.city}
                onChange={(value) => setFilter('city', value)}
              />

              <DealerTextFilter
                id="dealerFilter"
                label={t('Dashboard.Filters.Dealer', 'Dealer')}
                placeholder={t('Dashboard.Filters.FindDealer', 'Find a dealer')}
                clearTitle={t('Dashboard.Filters.Clear', 'Clear')}
                listId="dealerList"
                options={dealerNames}
                value={filters.dealer}
                onChange={(value) => setFilter('dealer', value)}
              />

              <button
                type="button"
                className={`country-thresholds-toggle-btn${thresholdsPanelOpen ? ' active' : ''}`}
                onClick={() => setThresholdsPanelOpen((open) => !open)}
              >
                <span>
                  {thresholdsPanelOpen
                    ? t('ThresholdsPanel.HideButton', 'Hide Thresholds')
                    : t('ThresholdsPanel.ToggleButton', 'Show Thresholds')}
                </span>
              </button>

              <CountryThresholdsPanel
                thresholdsByCountry={data?.thresholdsByCountry ?? {}}
                categories={AFTER_SALES_THRESHOLD_CATEGORIES}
                className="after-sales"
                open={thresholdsPanelOpen}
                onClose={() => setThresholdsPanelOpen(false)}
              />
            </div>
          </div>
        )}

        <div className="regional-main-content">
          <div className="map-container">
            <div className="map-controls">
              <button
                type="button"
                className="btn-map-control"
                onClick={() => setSidebarOpen((open) => !open)}
                title={t('Dashboard.Map.ToggleSidebar', 'Toggle sidebar')}
                aria-label={t('Dashboard.Map.SidebarAriaLabel', 'Toggle the filters sidebar')}
                aria-expanded={sidebarOpen}
              >
                <span aria-hidden="true">📋</span>
                <span>{t('Dashboard.Map.Sidebar', 'Sidebar')}</span>
              </button>
            </div>

            <StellantisDealerMap
              dealers={filteredDealers}
              categories={categories}
              labels={mapLabels}
              resizeToken={`${sidebarOpen}-${panelOpen}`}
            />

            {/*
             * The wave arrives whole, so until it does there is nothing to plot:
             * cover the map instead of showing it empty. Same overlay the regional
             * screen uses, and .map-container is its positioning context.
             */}
            {isLoading && (
              <div className="loading-overlay" role="status" aria-live="polite">
                <div className="spinner" aria-hidden="true" />
                <span className="visually-hidden">
                  {t('Dashboard.Map.Loading', 'Loading dealers…')}
                </span>
              </div>
            )}
          </div>

          <DealersPanel
            open={panelOpen}
            onToggle={() => setPanelOpen((open) => !open)}
            toolbar={
              <>
                <input
                  type="search"
                  className="regional-dealers-search"
                  value={tableSearch}
                  onChange={(event) => setTableSearch(event.target.value)}
                  placeholder={t('Grid.Search', 'Search:')}
                  aria-label={t('Grid.Search', 'Search:')}
                />

                <div className="dt-buttons">
                  <button
                    type="button"
                    className="btn-export-excel"
                    onClick={handleExport}
                    disabled={exporting}
                  >
                    {exporting
                      ? t('Grid.Exporting', 'Exporting…')
                      : t('Grid.ExportExcel', 'Export Excel')}
                  </button>
                </div>
              </>
            }
          >
            <DataGrid<Dealer>
              {...gridProps}
              columns={columns}
              isLoading={isLoading}
              tableClassName="regional-dealers-table"
              getRowId={(row, index) => `${row.physOut}-${row.brand}-${index}`}
              pageSizeOptions={[10, 25, 50, 100]}
              emptyMessage={t('Grid.EmptyTable', 'No dealers available')}
            />
          </DealersPanel>
        </div>
      </div>
    </div>
  );
}
